import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemBanco } from '../mensagemBanco';
import type { HistoricoPresenca } from '../types';

interface Props {
  encontroId: number;
  inscricaoId: number;
  nome: string;
  aoFechar: () => void;
}

type Estado = 'carregando' | 'pronto' | 'erro';

function formatarDataHora(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm} ${hh}:${min}`;
}

export default function ModalHistoricoPresenca({
  encontroId,
  inscricaoId,
  nome,
  aoFechar,
}: Props) {
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState<string | null>(null);
  const [itens, setItens] = useState<HistoricoPresenca[]>([]);

  useEffect(() => {
    let ativo = true;
    (async () => {
      setEstado('carregando');
      setErro(null);
      try {
        const { data, error } = await supabase.rpc('historico_da_presenca', {
          p_encontro: encontroId,
          p_inscricao: inscricaoId,
        });
        if (error) throw error;
        if (ativo) {
          setItens((data as HistoricoPresenca[]) || []);
          setEstado('pronto');
        }
      } catch (e) {
        if (ativo) {
          setErro(mensagemBanco(e));
          setEstado('erro');
        }
      }
    })();
    return () => {
      ativo = false;
    };
  }, [encontroId, inscricaoId]);

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 sm:items-center sm:p-6">
      <div className="animate-modal-pop flex max-h-[80vh] w-full max-w-md flex-col rounded-t-lg bg-white sm:rounded-lg">
        <div className="flex items-start justify-between gap-3 border-b border-secondary-100 p-5">
          <div className="min-w-0">
            <h3 className="text-base font-medium text-foreground-950">Histórico</h3>
            <p className="mt-0.5 truncate text-sm text-secondary-500">{nome}</p>
          </div>
          <button
            type="button"
            onClick={aoFechar}
            className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-secondary-500 transition-colors hover:bg-background-100 hover:text-foreground-800"
            aria-label="Fechar"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {estado === 'carregando' && (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-14 animate-pulse rounded bg-secondary-100" />
              ))}
            </div>
          )}

          {estado === 'erro' && (
            <p className="text-sm text-foreground-800">{erro}</p>
          )}

          {estado === 'pronto' && itens.length === 0 && (
            <p className="text-sm text-secondary-500">Nenhum registro ainda.</p>
          )}

          {estado === 'pronto' && itens.length > 0 && (
            <div className="space-y-4">
              {itens.map((it, i) => (
                <div key={i} className="relative pl-6">
                  <span className="absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full bg-primary-500" />
                  {i < itens.length - 1 && (
                    <span className="absolute left-1 top-4 h-full w-0.5 bg-secondary-200" />
                  )}
                  <p className="text-sm font-medium text-foreground-900">{it.acao}</p>
                  <p className="mt-0.5 text-xs text-secondary-500">
                    {formatarDataHora(it.quando)}
                    {it.quem_nome ? ` · por ${it.quem_nome}` : ''}
                  </p>
                  {it.de_status || it.para_status ? (
                    <p className="mt-0.5 text-xs text-secondary-500">
                      {it.de_status || '—'} → {it.para_status || '—'}
                    </p>
                  ) : null}
                  {it.motivo && (
                    <p className="mt-0.5 text-xs text-secondary-600">Motivo: {it.motivo}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}